/**
 * Client-side display-status derivation for `intent_relays` rows.
 *
 * Stored relay state is dispatch facts only (requested → dispatched |
 * failed); `satisfied` is server-computed in the read query and
 * excluded before rows reach this helper. STALENESS is derived here at
 * render time — no client timers (frontend-doctrine §8): a stale row
 * surfaces on the next natural render, mapped to the
 * `taskRelayUnconfirmed()` chip.
 *
 * Pure module: no Convex, no React, no praxis.
 */

/** Pending this long with no confirmation → show the unconfirmed chip. */
export const RELAY_STALE_MS = 180_000;

/**
 * Terminal/stuck rows older than this are deleted by the opportunistic
 * sweep inside the relay request mutations (no cron).
 */
export const RELAY_RETENTION_MS = 24 * 60 * 60 * 1000;

/**
 * Minimum spacing between a user's relay dispatches on the
 * **fallback-envelope-path only** (relay v2). The deterministic
 * intent-executor path needs no pacing — it serializes praxis commands
 * in-process — so request mutations schedule dispatch at `now` and only
 * `intentRelays.requeueForEnvelope` applies this spacing, when an intent
 * falls back to the LLM envelope path. Rationale for the value
 * (claw-doctrine §5.6.1 v2.42.3): the pod channel processes webhook
 * messages CONCURRENTLY in one shared session (in-flight limit 8, not
 * configurable), and a live burst incident showed the model skipping a
 * trigger whose sibling was mid-turn — 30 s exceeds the observed
 * 20–26 s turn time. Fallback bursts queue honestly under the §4.12
 * pending pattern.
 */
export const RELAY_DISPATCH_SPACING_MS = 30_000;

export type RelayStatus = "requested" | "dispatched" | "failed";

export type RelayDisplayStatus = "pending" | "stale" | "failed";

/**
 * Map an unsatisfied relay row to what the affordance renders. `failed`
 * wins outright; otherwise a row pending past `RELAY_STALE_MS` is
 * `stale` (unconfirmed chip), else `pending` (sky pulse).
 */
export function deriveRelayDisplayStatus({
  relay,
  now,
}: {
  relay: { readonly status: RelayStatus; readonly requestedAt: number };
  now: number;
}): RelayDisplayStatus {
  if (relay.status === "failed") return "failed";
  return now - relay.requestedAt > RELAY_STALE_MS ? "stale" : "pending";
}
